/**
 * 工程表を出力（工程管理の5回目、#104、docs/39 §7.3、Figma F1）。PC・タブレットはダイアログ、スマホ（768px 未満）は下からのシート
 *   左：見本（1枚目）と「A3 横・n枚」。形式・用紙・期間・入れるものを変えると、すぐ描き直す（Excel・CSV のときも紙の見本）
 *   右：形式（Excel（見た目のまま）／PDF／CSV）・注記・用紙・期間（「日付で選ぶ」で から・まで）・入れるもの・「やめる」「出力する」
 *   見本は packages/paper の schedulePaperModel・renderSchedulePaper（API の PDF と同じ関数）。shadow DOM に入れて画面の CSS と混ぜない
 *   出力する：Excel・CSV は API から受け取って保存、PDF は「紙は画面の中で見る」の画面（R13 の形）を開く
 *   選んだもの（形式・用紙・目盛・入れるもの）はその人のブラウザに覚える。金額（社内用）だけは覚えず、毎回外した状態から始める
 *   目盛（#290、docs/102 §2.1・#291 §2.1.1）：PDF のときだけ選べる（自動／日／旬／月。既定 自動）。Excel・CSV は日の列のまま＝見本も日の目盛
 *   見るだけのとき（タブレットの見るだけ・トライアルが終わったあと）も出せる（GET だけ）
 */
import {
  OVERVIEW_INCLUDE_KEYS,
  SCHEDULE_EXPORT_LIMITS,
  SCHEDULE_INCLUDE_DEFAULT,
  SCHEDULE_INCLUDE_KEYS,
  SCHEDULE_INCLUDE_LABEL,
  includeLabelOf,
  SCHEDULE_PAPERS,
  SCHEDULE_SCALES,
  barDatesDefault,
  scheduleScaleOf,
  SCHEDULE_SCALE_LABEL,
  includeParam,
  monthRangeOf,
  periodProblem,
  saveGateText,
  scheduleExportFileName,
  scheduleSpanOf,
  weekRangeOf,
  type CompanyOffDay,
  type Holiday,
  type ScheduleExportFormat,
  type ScheduleInclude,
  type ScheduleIncludeKey,
  type ScheduleLink,
  type ScheduleOverview,
  type SchedulePaperKey,
  type SchedulePeriodKind,
  type ScheduleScale,
  type ScheduleRow,
  type ScheduleRowDay,
  type ScheduleStop,
} from "@genbaryoku/contract";
import { SCHEDULE_EXPORT_MSG as X } from "./msg.js";
import { renderSchedulePaper, schedulePaperModel, type SchedulePaperModel, type SchedulePaperSource } from "@genbaryoku/paper";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { api, ApiError, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { ErrorText, Switch } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { currentProduct, productHeaders } from "../../lib/product.js";
import { DateButton, useEsc } from "./Dialogs.jsx";
import { useWide } from "./hooks.js";
import { todayLocal } from "./timeline.js";

/** 案件の工程表から出す：いま開いている工程表（保存していない直しも入った形）と案件の中身 */
export type GroupExportTarget = {
  kind: "group";
  /** 案件（案件なしの{schedule}は null。#291） */
  groupId: string | null;
  scheduleId: string;
  subject: string;
  addressee: string;
  staff: string;
  schedule: {
    rows: ScheduleRow[];
    links: ScheduleLink[];
    offWeekdays: readonly number[];
    holidayOff: boolean;
    useCompanyOffDays: boolean;
    stops: readonly ScheduleStop[];
    rowDays?: readonly ScheduleRowDay[];
    /** {siteShort}の作業日（#296） */
    workDays?: readonly string[];
  };
  holidays: readonly Holiday[];
  companyOffDays: readonly CompanyOffDay[];
  /** 会社の設定「進捗を％で入力」（#292。false なら入れるもの「状況」・見本の列「状況」） */
  progressPct?: boolean;
};
/** 全体の工程表から出す：いまの並べ方・探す・開いている現場・見ている期間 */
export type OverviewExportTarget = { kind: "overview"; mode: "site" | "vendor"; q: string; open: readonly string[]; view: { from: string; to: string } };
export type ExportTarget = GroupExportTarget | OverviewExportTarget;

const PREF_KEY = "gb.sch.export";
/** wideName＝「{task}の列を広く」（#300。入れるものと同じ所に覚える） */
type Pref = { format: ScheduleExportFormat; paper: SchedulePaperKey; scale: ScheduleScale; include: Partial<Record<ScheduleIncludeKey, boolean>>; wideName: boolean };
function readPref(): Pref | null {
  try {
    const raw = window.localStorage.getItem(PREF_KEY);
    const p = raw ? (JSON.parse(raw) as Partial<Pref>) : null;
    if (!p) return null;
    return {
      format: p.format === "pdf" || p.format === "csv" || p.format === "xlsx" ? p.format : "xlsx",
      paper: p.paper && p.paper in SCHEDULE_PAPERS ? p.paper : "a3l",
      // 前の値 week（#290 の週）は旬（#291 §2.1.1）
      scale: scheduleScaleOf(p.scale) ?? "auto",
      include: p.include && typeof p.include === "object" ? p.include : {},
      wideName: p.wideName === true,
    };
  } catch {
    return null;
  }
}
function writePref(p: Pref) {
  try {
    // 金額（社内用）は覚えない（docs/39 §7.2 の6）。バーの日付は目盛で既定が変わるので覚えない（#291 §2.1.1）
    const { amount: _amount, barDates: _barDates, ...include } = p.include;
    window.localStorage.setItem(PREF_KEY, JSON.stringify({ ...p, include }));
  } catch {
    /* 覚えられなくても出せる */
  }
}

/** 出力の問い合わせ（API の export と、PDF の紙の画面が同じものを使う） */
export function exportQuery(t: ExportTarget, o: { format: ScheduleExportFormat; paper: SchedulePaperKey; scale?: ScheduleScale; from: string | null; to: string | null; include: ScheduleInclude; wideName?: boolean }): string {
  const qs = new URLSearchParams();
  qs.set("format", o.format);
  qs.set("paper", o.paper);
  // 目盛は PDF だけ（省けば API は日）
  if (o.format === "pdf" && o.scale) qs.set("scale", o.scale);
  if (o.from && o.to) {
    qs.set("from", o.from);
    qs.set("to", o.to);
  }
  // 備考は概略のときだけ（#291。概略でなければ URL に入れない＝前からの URL と同じ）
  qs.set("include", includeParam({ ...o.include, note: o.include.summary && o.include.note }, includeKeysOf(t)));
  // 「{task}の列を広く」（#300）は ON のときだけ（OFF は前からの URL と同じ）
  if (o.wideName) qs.set("wideName", "1");
  if (t.kind === "overview") {
    qs.set("mode", t.mode);
    if (t.q.trim()) qs.set("q", t.q.trim());
    if (t.open.length) qs.set("open", t.open.join(","));
  }
  return qs.toString();
}
/** 入れるものの選べるもの：案件の工程表は全部・全体の工程表は 開始・日数（{siteShort}別だけ）・人数・今日の線 */
export const includeKeysOf = (t: ExportTarget): readonly ScheduleIncludeKey[] =>
  t.kind === "group" ? SCHEDULE_INCLUDE_KEYS : t.mode === "site" ? OVERVIEW_INCLUDE_KEYS : OVERVIEW_INCLUDE_KEYS.filter((k) => k !== "start" && k !== "days");
export const exportUrl = (t: ExportTarget, qs: string) =>
  t.kind === "group" ? (t.groupId ? `/api/v1/groups/${t.groupId}/schedule/export?${qs}` : `/api/v1/schedules/${t.scheduleId}/export?${qs}`) : `/api/v1/schedule/overview/export?${qs}`;

/** 紙の見本（shadow DOM に紙の CSS ごと入れる。幅に合わせて縮める） */
export function PaperPreview({ model, onClick, testId }: { model: SchedulePaperModel | null; onClick?: (() => void) | undefined; testId?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(480);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    // 枠の余白（padding）を除いた幅で縮める（含めると紙の右が枠で切れ、表が右端まで続くように見えた。10/1 射場さん）
    const inner = () => {
      const cs = getComputedStyle(el);
      return el.clientWidth - parseFloat(cs.paddingLeft || "0") - parseFloat(cs.paddingRight || "0");
    };
    const ro = new ResizeObserver(() => setW(inner()));
    ro.observe(el);
    setW(inner());
    return () => ro.disconnect();
  }, []);
  const out = useMemo(() => (model ? renderSchedulePaper(model, { pages: [0] }) : null), [model]);
  useEffect(() => {
    const h = host.current;
    if (!h) return;
    const root = h.shadowRoot ?? h.attachShadow({ mode: "open" });
    root.innerHTML = out ? `<style>${out.css}</style><div class="sp-page">${out.pages[0] ?? ""}</div>` : "";
  }, [out]);
  const pxW = (model?.paper.w ?? 420) * 3.7795;
  const pxH = (model?.paper.h ?? 297) * 3.7795;
  const k = Math.max(0.05, w / pxW);
  return (
    <div ref={box} className={"sch-xprev" + (onClick ? " tap" : "")} onClick={onClick} data-testid={testId} aria-label={X.preview}>
      <div className="sheet" style={{ width: pxW * k, height: pxH * k }}>
        <div ref={host} className="host" style={{ width: pxW, height: pxH, transform: `scale(${k})`, transformOrigin: "0 0" }} />
      </div>
    </div>
  );
}

export function ExportDialog({
  target,
  onClose,
  toast,
  beforeExport,
}: {
  target: ExportTarget;
  onClose: () => void;
  toast: (t: string) => void;
  /** 出す前に書きかけの保存を送り終えるのを待つ（案件の工程表。#179）。保存できていなければ理由の文 */
  beforeExport?: () => Promise<string | null>;
}) {
  const phone = !useWide(768);
  const me = useMe().data;
  const L = useLabels();
  const nav = useNavigate();
  const today = useMemo(() => todayLocal(), []);
  const pref = useMemo(() => readPref(), []);
  const allowed = includeKeysOf(target);
  const [format, setFormat] = useState<ScheduleExportFormat>(pref?.format ?? "xlsx");
  const [paper, setPaper] = useState<SchedulePaperKey>(pref?.paper ?? "a3l");
  const [scale, setScale] = useState<ScheduleScale>(pref?.scale ?? "auto");
  const [include, setInclude] = useState<ScheduleInclude>(() => {
    const inc = { ...SCHEDULE_INCLUDE_DEFAULT };
    for (const k of allowed) if (k !== "amount" && pref?.include[k] != null) inc[k] = !!pref.include[k];
    inc.amount = false; // 毎回外した状態から
    inc.barDates = false; // 既定は決まった目盛で（下の barDates）
    return inc;
  });
  // バーの日付（#291 §2.1.1）：既定は 旬・月は ON・日は OFF（自動は決まった目盛で）。手で変えたらその値
  const [barSet, setBarSet] = useState<boolean | null>(null);
  // 「{task}の列を広く」（#300）：既定 OFF・覚える
  const [wideName, setWideName] = useState<boolean>(pref?.wideName ?? false);
  const periodKinds: SchedulePeriodKind[] = target.kind === "group" ? ["all", "month", "week", "custom"] : ["view", "month", "week", "custom"];
  const [period, setPeriod] = useState<SchedulePeriodKind>(periodKinds[0]!);
  const span = target.kind === "group" ? scheduleSpanOf(target.schedule.rows) : null;
  const base = target.kind === "group" ? (span ?? weekRangeOf(today)) : target.view;
  const [custom, setCustom] = useState<{ from: string; to: string }>(base);
  const range = period === "all" || period === "view" ? base : period === "month" ? monthRangeOf(today) : period === "week" ? weekRangeOf(today) : custom;
  const problem = periodProblem(range.from, range.to);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const [big, setBig] = useState(false);
  useEsc(() => (big ? setBig(false) : onClose()));
  useEffect(() => writePref({ format, paper, scale, include, wideName }), [format, paper, scale, include, wideName]);

  // 全体の工程表は、選んだ期間の材料を API から（全体の工程表の画面と同じ問い合わせ。控えがあればそれを使う）
  const ovQ = useQuery({
    queryKey: ["schedule-overview", range.from, range.to],
    queryFn: () => api<ScheduleOverview>("GET", `/schedule/overview?from=${range.from}&to=${range.to}`),
    enabled: target.kind === "overview" && !problem,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const baseModel = useMemo((): SchedulePaperModel | null => {
    if (problem) return null;
    const company = me?.tenant.name ?? "";
    // 会社の言葉と製品（#142 段5）：見本の紙の題・列も API の PDF と同じ言葉に
    const vocab = { labels: L, product: me?.tenant.product ?? currentProduct() };
    let src: SchedulePaperSource;
    if (target.kind === "group") {
      src = {
        kind: "group",
        vocab,
        subject: target.subject,
        labels: { subject: L.subject, period: L.period },
        addressee: target.addressee,
        company,
        staff: target.staff,
        today,
        schedule: include.amount ? target.schedule : { ...target.schedule, rows: target.schedule.rows.map((r) => ({ ...r, amount: null })) },
        holidays: target.holidays,
        companyOffDays: target.companyOffDays,
        progressPct: target.progressPct ?? true,
      };
    } else {
      const ov = ovQ.data;
      if (!ov || ov.from !== range.from || ov.to !== range.to) return null;
      src = { kind: "overview", vocab, mode: target.mode, q: target.q, open: target.open, ov, company, today };
    }
    // 目盛は PDF だけ（Excel・CSV は日の列＝見本も日。API と同じ）
    return schedulePaperModel(src, { paper, from: range.from, to: range.to, include, scale: format === "pdf" ? scale : "day", wideName });
  }, [target, include, paper, scale, format, range.from, range.to, problem, me?.tenant.name, me?.tenant.product, L, today, ovQ.data, wideName]);
  // バーの日付は紙の形を変えない（バーの横に字を足すだけ）＝目盛が決まった紙に重ねる。PDF だけ
  const barDates = format === "pdf" && (barSet ?? (baseModel ? barDatesDefault(baseModel.scale) : false));
  const model = useMemo((): SchedulePaperModel | null => (baseModel ? { ...baseModel, include: { ...baseModel.include, barDates } } : null), [baseModel, barDates]);
  const includeOut: ScheduleInclude = { ...include, barDates };

  const limit =
    problem ??
    (target.kind === "overview" && ovQ.data?.truncated ? X.overviewTooMany : null) ??
    (model && model.rows.length > SCHEDULE_EXPORT_LIMITS.rows ? X.tooManyRows(model.rows.length, target.kind === "overview") : null) ??
    (model && model.pages.length > SCHEDULE_EXPORT_LIMITS.pages ? X.tooManyPages(model.pages.length) : null);
  const empty = target.kind === "group" && !target.schedule.rows.length;

  const submit = async () => {
    if (!model || limit || empty) return;
    if (beforeExport) {
      // 出力は保存してある中身から作る＝書きかけを送り終えてから（送れなければ出さずに理由。#179）
      setBusy(true);
      setErr(undefined);
      const why = await beforeExport();
      setBusy(false);
      if (why) {
        setErr(saveGateText("export", why));
        return;
      }
    }
    const qs = exportQuery(target, { format, paper, scale, from: range.from, to: range.to, include: includeOut, wideName });
    if (format === "pdf") {
      onClose();
      nav(target.kind === "group" ? (target.groupId ? `/groups/${target.groupId}/schedule/paper?${qs}` : `/schedules/${target.scheduleId}/paper?${qs}`) : `/schedule/paper?${qs}`);
      return;
    }
    setBusy(true);
    setErr(undefined);
    try {
      const res = await fetch(exportUrl(target, qs), { credentials: "include", headers: productHeaders() });
      if (!res.ok) {
        const p = (await res.json().catch(() => ({ title: X.failed }))) as { title?: string; code?: string };
        throw new ApiError(res.status, { type: "about:blank", status: res.status, title: p.title ?? X.failed, ...(p.code ? { code: p.code } : {}) });
      }
      const blob = await res.blob();
      const name = scheduleExportFileName(target.kind === "group" ? { group: target.subject } : { overview: target.mode }, today, format, t);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast(X.done(name));
      onClose();
    } catch (e) {
      setErr(e instanceof ApiError ? messageOf(e) : X.failed);
    } finally {
      setBusy(false);
    }
  };

  const title = X.title;
  const sub = target.kind === "group" ? target.subject : t(target.mode === "vendor" ? "{contractor}別" : "{siteShort}別");
  const caption = model ? X.sheets(SCHEDULE_PAPERS[paper].label, model.pages.length) : "";

  const options = (
    <div className="sch-xopts">
      <fieldset className="sch-xset">
        <legend>{X.format}</legend>
        <div className="sch-xcards" role="radiogroup" aria-label={X.format}>
          {(["xlsx", "pdf", "csv"] as const).map((f) => (
            <button key={f} type="button" role="radio" aria-checked={format === f} className={"sch-xcard" + (format === f ? " on" : "")} onClick={() => setFormat(f)} data-testid={`sch-x-format-${f}`}>
              <span className="t">
                <b>{X.formats[f].name}</b>
                <small>{X.formats[f].sub}</small>
              </span>
              <span className="dot" aria-hidden="true" />
            </button>
          ))}
        </div>
        <p className="hint sch-xnote" data-testid="sch-x-note">
          {format === "xlsx" ? X.excelNote : format === "csv" ? X.csvNote : ""}
        </p>
      </fieldset>
      <fieldset className="sch-xset">
        <legend>{X.paper}</legend>
        <div className="sch-seg sch-xseg" role="radiogroup" aria-label={X.paper}>
          {(Object.keys(SCHEDULE_PAPERS) as SchedulePaperKey[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={paper === k} className={paper === k ? "on" : ""} onClick={() => setPaper(k)} data-testid={`sch-x-paper-${k}`}>
              {SCHEDULE_PAPERS[k].label}
            </button>
          ))}
        </div>
      </fieldset>
      {format === "pdf" ? (
        <fieldset className="sch-xset" data-testid="sch-x-scale">
          <legend>{X.scale}</legend>
          <div className="sch-seg sch-xseg" role="radiogroup" aria-label={X.scale}>
            {SCHEDULE_SCALES.map((k) => (
              <button key={k} type="button" role="radio" aria-checked={scale === k} className={scale === k ? "on" : ""} onClick={() => setScale(k)} data-testid={`sch-x-scale-${k}`}>
                {SCHEDULE_SCALE_LABEL[k]}
              </button>
            ))}
          </div>
        </fieldset>
      ) : null}
      <fieldset className="sch-xset">
        <legend>{X.period}</legend>
        <div className="sch-seg sch-xseg" role="radiogroup" aria-label={X.period}>
          {periodKinds.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={period === k}
              className={period === k ? "on" : ""}
              onClick={() => {
                if (k === "custom" && period !== "custom") setCustom(range);
                setPeriod(k);
              }}
              data-testid={`sch-x-period-${k}`}
            >
              {X.periods[k]}
            </button>
          ))}
        </div>
        {period === "custom" ? (
          <div className="sch-xdates">
            <DateButton value={custom.from} label={X.from} onChange={(v) => setCustom((c) => ({ ...c, from: v }))} testId="sch-x-from" />
            <DateButton value={custom.to} label={X.to} onChange={(v) => setCustom((c) => ({ ...c, to: v }))} testId="sch-x-to" />
          </div>
        ) : null}
      </fieldset>
      <fieldset className="sch-xset">
        <legend>{X.include}</legend>
        <div className="sch-xinc">
          {/* 備考は概略のときだけ選べる（#291）。概略を ON にすると 開始・日数を外す（手で ON にできる） */}
          {/* バーの日付（#291 §2.1.1）は PDF だけ */}
          {allowed.filter((k) => (k !== "note" || include.summary) && (k !== "barDates" || format === "pdf")).map((k) => (
            <label key={k} className="ck">
              <input
                type="checkbox"
                checked={k === "barDates" ? barDates : include[k]}
                onChange={(e) => {
                  const on = e.target.checked;
                  if (k === "barDates") return setBarSet(on);
                  setInclude((x) => (k === "summary" ? { ...x, summary: on, ...(on ? { start: false, days: false } : { start: true, days: true }) } : { ...x, [k]: on }));
                }}
                data-testid={`sch-x-inc-${k}`}
              />
              {includeLabelOf(k, target.kind === "group" ? (target.progressPct ?? true) : (ovQ.data?.company.progressPct ?? true))}
            </label>
          ))}
        </div>
        {/* 「{task}の列を広く」（#300）：1列目の見出しの言葉で（概略は{trade}・全体は{siteShort}・{task}／{contractor}・担当）。CSV は列の幅が無いので出さない */}
        {format !== "csv" ? (
          <div className="sch-xwide">
            <Switch checked={wideName} onChange={setWideName} label={X.wideName(target.kind === "group" ? (include.summary ? X.colTrade : X.colName) : target.mode === "vendor" ? X.colPerson : X.colSite)} testId="sch-x-widename" />
          </div>
        ) : null}
      </fieldset>
      <ErrorText>{limit ?? err ?? (target.kind === "overview" && ovQ.isError ? messageOf(ovQ.error) : undefined)}</ErrorText>
    </div>
  );
  const foot = (
    <>
      <span className="sp" />
      <button type="button" className="btn" onClick={onClose}>
        {X.cancel}
      </button>
      <button type="button" className="btn primary" onClick={() => void submit()} disabled={busy || !model || !!limit || empty} data-testid="sch-x-submit">
        {busy ? X.making : X.submit}
      </button>
    </>
  );
  const preview = (
    <div className="sch-xleft">
      <PaperPreview model={model} onClick={phone ? () => setBig(true) : undefined} testId="sch-x-preview" />
      <p className="hint sch-xcap" data-testid="sch-x-sheets">
        {caption}
      </p>
    </div>
  );

  return createPortal(
    <>
      {phone ? (
        <div className="dim sch-sheetdim" onClick={(e) => e.target === e.currentTarget && onClose()}>
          <div className="sch-sheet sch-xsheet" role="dialog" aria-modal="true" aria-label={title} data-testid="sch-export" data-notice-above="">
            <span className="grab" aria-hidden="true" />
            <div className="hd">
              <b>{title}</b>
              <small>{sub}</small>
            </div>
            <div className="sch-xbody">
              {preview}
              {options}
            </div>
            <div className="sch-linkfoot">{foot}</div>
          </div>
        </div>
      ) : (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
          <div className="dlg sch-xdlg" role="dialog" aria-modal="true" aria-label={title} data-testid="sch-export">
            <div className="dh sch-dh">
              {title}
              <small className="sch-xsub">{sub}</small>
              <span className="sp" />
              <button type="button" className="btn text sq" aria-label={X.cancel} onClick={onClose}>
                ×
              </button>
            </div>
            <div className="db sch-xbody">
              {preview}
              {options}
            </div>
            <div className="df">{foot}</div>
          </div>
        </div>
      )}
      {big ? (
        <div className="dim sch-xbig" onClick={() => setBig(false)} data-testid="sch-x-big">
          <PaperPreview model={model} />
          <button type="button" className="btn" onClick={() => setBig(false)}>
            {X.previewClose}
          </button>
        </div>
      ) : null}
    </>,
    document.body,
  );
}
